import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
} from 'react';
import {decisionTree} from '../../_common/constants/decisionTree';
import {useTempInspectionsListContext} from './TempInspectionsListContext';
import {FormikProps} from 'formik';

type Inspection = {
  shumaTypeID: number | null;
  propertyTypeID: number | null;
  loanTypeID: number | null;
  loanSubTypeID: number | null;
  otherPropertyTypeID: number | null;
};

type FormValues = {
  inspections: Inspection[];
};

type FieldNames = Record<string, string>;

export type TempInspectionContextType = {
  formikTempInspection: FormikProps<FormValues>;
  decisionTreeProps: {
    decisionTree: typeof decisionTree;
    decisionTreeOptions: { disabled: string[] };
    handleResetDecisionTree: (value: string, fieldNames: FieldNames) => void;
    handleResetPreviousSteps: (
      fieldId: string,
      value: unknown,
      fieldNames: FieldNames,
      values: FormValues
    ) => void;
    showBankData: boolean;
    setShowBankData: React.Dispatch<React.SetStateAction<boolean>>;
  };
  fieldNames: FieldNames;
};

type ValidateForm = {
  fieldId: string;
  value: unknown;
  fieldNames: FieldNames;
  formValues: FormValues;
  validateForm: FormikProps<FormValues>['validateForm'];
  values: FormValues;
  setErrors: FormikProps<FormValues>['setErrors'];
};

const TempInspectionContext = createContext<TempInspectionContextType | null>(null);

export function useTempInspectionContext(): TempInspectionContextType {
  const ctx = useContext(TempInspectionContext);
  if (!ctx) throw new Error('TempInspectionContext not found');
  return ctx;
}

type Props = {
  index: number;
  children: ReactNode;
};

export function TempInspectionProvider({
                                         index,
                                         children,
                                       }: Props) {
  const [showBankData, setShowBankData] = useState(false);

  const {formikTempInspections} = useTempInspectionsListContext();

  const decisionTreeOptions = {
    disabled: [] as string[],
  };

  const handleResetDecisionTree = (
    value: string,
    fieldNames: FieldNames
  ) => {
    const {
      bankID,
      shumaTypeID,
      propertyTypeID,
      loanTypeID,
      loanText,
      otherPropertyTypeID,
      loanSubTypeID,
    } = fieldNames;
    const {setFieldValue, validateForm} = formikTempInspections;
    switch (value) {
      case decisionTree.bankSecure:
        setFieldValue(bankID, null);
        setFieldValue(shumaTypeID, null);
        setFieldValue(propertyTypeID, null);
        setFieldValue(loanTypeID, null);
        setFieldValue(loanSubTypeID, null);
        setFieldValue(loanText, null);
        setFieldValue(otherPropertyTypeID, null);
      case decisionTree.rightsEstimation:
      default:
        setFieldValue(shumaTypeID, null);
        setFieldValue(propertyTypeID, null);
    }

    validateForm();
  };

  const handleResetPreviousSteps = (
    fieldId: string,
    value: unknown,
    fieldNames: FieldNames,
    values: FormValues
  ) => {
    const {
      bankID,
      shumaTypeID,
      propertyTypeID,
      loanTypeID,
      loanSubTypeID,
      loanText,
      otherPropertyTypeID,
      bankBranchNumber,
      bankBranchName,
      bankClerkFullName,
      bankEmail,
      bankRequestID,
      bankBuildType,
    } = fieldNames;

    const {setFieldValue} = formikTempInspections;

    const shumaTypeIDFieldEnabled = !decisionTreeOptions.disabled.includes(shumaTypeID);

    switch (fieldId) {
      case bankID:
        if (shumaTypeIDFieldEnabled) {
          setFieldValue(bankBranchNumber, '');
          setFieldValue(bankBranchName, '');
          setFieldValue(bankClerkFullName, '');
          setFieldValue(bankEmail, '');
          setFieldValue(bankRequestID, '');
          setFieldValue(bankBuildType, '');
          setFieldValue(shumaTypeID, null);
        }
      case shumaTypeID:
        if (shumaTypeIDFieldEnabled) {
          setFieldValue(propertyTypeID, null);
        }
      case propertyTypeID:
        if (shumaTypeIDFieldEnabled) {
          setFieldValue(loanTypeID, null);
        }
      case loanTypeID:
      default:
        if (shumaTypeIDFieldEnabled) {
          setFieldValue(loanText, '');
          setFieldValue(loanSubTypeID, null);
          setFieldValue(otherPropertyTypeID, null);
        }
    }

    validateUpdatedForm({
      fieldId,
      value,
      fieldNames,
      formValues: values,
      ...formikTempInspections,
    });
  };

  const validateUpdatedForm = ({
                                 fieldId,
                                 value,
                                 fieldNames,
                                 formValues,
                                 validateForm,
                                 values,
                                 setErrors,
                               }: ValidateForm) => {
    const updatedValues = {...values, [fieldId]: value};

    const {
      bankID,
      shumaTypeID,
      propertyTypeID,
      loanTypeID,
      loanSubTypeID,
      otherPropertyTypeID,
    } = fieldNames;

    switch (fieldId) {
      case bankID:
        // @ts-ignore
        updatedValues[shumaTypeID] = formValues.inspections?.[index]?.shumaTypeID;
      case shumaTypeID:
        // @ts-ignore
        updatedValues[propertyTypeID] = formValues.inspections?.[index]?.propertyTypeID;
      case propertyTypeID:
        // @ts-ignore
        updatedValues[loanTypeID] = formValues.inspections?.[index]?.loanTypeID;
      case loanTypeID:
      default:
        // @ts-ignore
        updatedValues[loanSubTypeID] = formValues.inspections?.[index]?.loanSubTypeID;
        // @ts-ignore
        updatedValues[otherPropertyTypeID] = formValues.inspections?.[index]?.otherPropertyTypeID;
    }

    validateForm(updatedValues).then(setErrors);
  };

  const fieldNames: FieldNames = {
    propertyTypeIDRightsEstimation: `inspections.${index}.propertyTypeID-rightsEstimation`,
    bankID: `inspections.${index}.bankID`,
    shumaTypeID: `inspections.${index}.shumaTypeID`,
    propertyTypeID: `inspections.${index}.propertyTypeID`,
    loanTypeID: `inspections.${index}.loanTypeID`,
    loanText: `inspections.${index}.loanText`,
    otherPropertyTypeID: `inspections.${index}.otherPropertyTypeID`,
    shumaReasonID: `inspections.${index}.shumaReasonID`,
    bankBranchNumber: `inspections.${index}.bankBranchNumber`,
    bankBranchName: `inspections.${index}.bankBranchName`,
    bankClerkFullName: `inspections.${index}.bankClerkFullName`,
    bankEmail: `inspections.${index}.bankEmail`,
    bankRequestID: `inspections.${index}.bankRequestID`,
    bankBuildType: `inspections.${index}.bankBuildType`,
    responsibleUser: `inspections.${index}.responsibleUser`,
    dueDate: `inspections.${index}.dueDate`,
    city: `inspections.${index}.city`,
    gush: `inspections.${index}.gush`,
    street: `inspections.${index}.street`,
    helka: `inspections.${index}.helka`,
    houseNumber: `inspections.${index}.houseNumber`,
    ttHelka: `inspections.${index}.ttHelka`,
    apartmentNumber: `inspections.${index}.apartmentNumber`,
    notes: `inspections.${index}.notes`,
    landLotNum: `inspections.${index}.landLotNum`,
    landLotNumAccordingTaba: `inspections.${index}.landLotNumAccordingTaba`,
    loanSubTypeID: `inspections.${index}.loanSubTypeID`,
    shumaMazminIdentityID: `inspections.${index}.shumaMazminIdentityID`,
    underConstruction: `inspections.${index}.underConstruction`,
    purchaseCompensation: `inspections.${index}.purchaseCompensation`,
    tempShumaID: `inspections.${index}.tempShumaID`,
  };

  const value: TempInspectionContextType = {
    // @ts-ignore
    formikTempInspection: formikTempInspections,
    decisionTreeProps: {
      decisionTree,
      decisionTreeOptions,
      handleResetDecisionTree,
      handleResetPreviousSteps,
      showBankData,
      setShowBankData,
    },
    fieldNames,
  };

  return (
    <TempInspectionContext.Provider value={value}>
      {children}
    </TempInspectionContext.Provider>
  );
}